import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type AskTab = "ask" | "mine";
export const OPEN_ASK_EVENT = "repoworks:open-ask";
/** ``?ask=requests`` opens My requests (the link of a request
 *  notification); the parameter is removed once read. */
export const ASK_QUERY_PARAM = "ask";

export function openAskPanel(tab: AskTab = "ask"): void {
  window.dispatchEvent(new CustomEvent<AskTab>(OPEN_ASK_EVENT, { detail: tab }));
}

/** Ctrl+/ (⌘/ on macOS). Works in text fields too: it is modified, so it
 *  never collides with typing (unlike ``?``, which opens the shortcuts). */
export function isAskShortcut(e: KeyboardEvent): boolean {
  return e.key === "/" && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
}

export function useAskPanelState(enabled: boolean) {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<AskTab>("ask");
  const location = useLocation();
  const navigate = useNavigate();

  const openAt = React.useCallback((next: AskTab) => {
    setTab(next);
    setOpen(true);
  }, []);
  const close = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isAskShortcut(e)) return;
      e.preventDefault();
      openAt("ask");
    };
    const onOpen = (e: Event) => openAt((e as CustomEvent<AskTab>).detail ?? "ask");
    document.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_ASK_EVENT, onOpen);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_ASK_EVENT, onOpen);
    };
  }, [enabled, openAt]);

  React.useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams(location.search);
    if (params.get(ASK_QUERY_PARAM) !== "requests") return;
    params.delete(ASK_QUERY_PARAM);
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
    // Opening from a notification link is an external event (the URL),
    // like the drawer's close-on-route-change in AppShell.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    openAt("mine");
  }, [enabled, location.pathname, location.search, location.hash, navigate, openAt]);

  return { open, tab, setTab, openAt, close };
}
